import React from 'react';

import {
    setAddWidgetModal,
    setDropdownOptions,
    setWidgetDeviceTypes,
    resetWidget,
    setPayload,
} from '@fiji/common/src/features/widgetManagement/widgetSlice';
import { useNavigate } from 'react-router-dom';
import { setDashboardWidgetIds } from '@fiji/common/src/features/dashboardManagement/dashboardSlice';
import { useCreateWidgetMutation } from '@fiji/common/src/features/widgetManagement/widgetApi';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { isNullOrUndefined, getWidgetTypeValidationConfig } from '@fiji/common/src/utils/helpers';
import {
    useGetAllDeviceCategoriesMutation,
    useGetDeviceModelsByCategoryMutation,
} from '@fiji/common/src/features/deviceManagement/ontologyApi';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { usePayloadActions } from '@fiji/common/src/hooks/usePayloadActions';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useRBAC, useNewCustomTranslations } from 'hooks';
import { useTranslation } from 'react-i18next';

type ReturnProps = {
    handleIsSelected: (type: any, selectedItem: any) => boolean;
    handleNextClick: any;
    fetchDeviceTypes: (key: string, type?: string, searchParams?: any) => Promise<void>;
    deviceTypesLoader: boolean;
    widgetCreationLoader: boolean;
    handleSubmit: (callback: any) => Promise<void>;
    widgetError?: any;
};

const withDefinedProp = (key: string, value: any): any => (value === undefined ? {} : { [key]: value });

const getPayloadSourceId = (payload: any, category: any): string | undefined =>
    payload?.sourceId || category?.profileId;

const getReferenceWidgetId = (selectedWidgetId: string, isNewWidget: boolean): string | undefined =>
    isNewWidget ? undefined : selectedWidgetId;

const buildWidgetCreatePayload = (payload: any, category: any): any => {
    const widgetType = payload?.widgetType ?? {};
    const selectedWidgetId = payload?.selectedWidget?.id;
    const isNewWidget = selectedWidgetId === 'newWidget';
    const sourceId = getPayloadSourceId(payload, category);
    const config = isNewWidget ? widgetType.config : undefined;
    const referenceWidgetId = getReferenceWidgetId(selectedWidgetId, isNewWidget);

    return {
        type: widgetType.type ?? 'PREDEFINED',
        widgetTypeId: widgetType.id,
        name: payload?.name,
        isSystemWidget: payload?.isSystemWidget,
        categoryId: category?.id,
        description: payload.description,
        ...withDefinedProp('config', config),
        ...withDefinedProp('sourceId', sourceId),
        ...withDefinedProp('referenceWidgetId', referenceWidgetId),
    };
};

export const useHandleAddWidgets = (): ReturnProps => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [cleanPayload] = usePayloadActions();

    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);

    const { payload, addWidgetModal: modalControls, category } = useTypedSelector((state) => state.widget);

    const [createWidget, { data: widgetCreationResponse, isLoading: widgetCreationLoader, error: widgetError }] =
        useCreateWidgetMutation();

    const [getDeviceTypesCategories, { isLoading }] = useGetAllDeviceCategoriesMutation();
    const [getDeviceModels, { isLoading: modelLoader }] = useGetDeviceModelsByCategoryMutation();

    const permissions = useTypedSelector(selectCurrentPermission);

    const { hasPermission } = useRBAC(permissions);
    const canCreateUserWidget = hasPermission('widget-management_view-user-widgets');
    const canCreateSystemWidget = hasPermission('widget-management_create-system-widgets');
    useNewCustomTranslations('ADD_DEVICE_FORM');

    React.useEffect(() => {
        if (widgetCreationResponse) {
            navigate(`/${currentRealmName}/editWidget/${widgetCreationResponse?.data?.id}`, {
                state: {
                    typeId: widgetCreationResponse?.data?.widgetType?.id,
                    mode: modalControls?.mode,
                    assignType: modalControls?.assignType,
                    assignId: modalControls?.assignId,
                    dashboardId: modalControls?.dashboardId,
                },
            });
        }
    }, [widgetCreationResponse]);

    React.useEffect(() => {
        if (canCreateUserWidget && !canCreateSystemWidget) {
            dispatch(setPayload({ key: 'isSystemWidget', value: false }));
        }
    }, [canCreateSystemWidget, canCreateUserWidget]);

    const handleIsSelected = (type: any, selectedItem: any): boolean => {
        if (!payload) return false;
        const payloadClone = JSON.parse(JSON.stringify(payload));
        return JSON.stringify(payloadClone[type]) === JSON.stringify(selectedItem);
    };

    const handleNextClick = (sourceModalCallback: any, newWidgetModalCallback: any): void => {
        dispatch(setAddWidgetModal({ ...modalControls, isOpen: false }));

        const isNewWidget = payload?.selectedWidget?.id === 'newWidget';
        const isSourceRequired = getWidgetTypeValidationConfig(payload?.widgetType?.id)['isSourceRequired'];
        const hasExternalWidgetScript = !isNullOrUndefined(payload?.widgetType?.internalCdnJsLink);
        const needsSourceModal = isNewWidget && isSourceRequired && !hasExternalWidgetScript;
        const isDashboardModeExisting = modalControls?.mode === 'dashboard' && !isNewWidget;

        if (needsSourceModal) {
            sourceModalCallback(true);
            return;
        }

        if (isDashboardModeExisting) {
            dispatch(setDashboardWidgetIds(payload.selectedWidget?.id));
            dispatch(resetWidget());
            return;
        }

        newWidgetModalCallback(true);
    };

    const handleSubmit = async (callback: any): Promise<void> => {
        const { error }: any = await createWidget(cleanPayload(buildWidgetCreatePayload(payload, category)));
        if (!error) {
            callback();
            dispatch(resetWidget());
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('ADD_DEVICE_FORM:DEVICE_SAVED_SUCCESSFULLY', { replace: { deviceName: payload?.name } }),
                })
            );
        }
    };

    const fetchDeviceTypes = async (key: string, type?: string, searchParams?: any): Promise<void> => {
        if (type === 'list') {
            const { data }: any = await getDeviceModels({
                filter: key,
                ...searchParams,
            });
            if (data) {
                dispatch(setWidgetDeviceTypes(data?.data));
            }
            return;
        }

        const { data }: any = await getDeviceTypesCategories({
            category: 'CATEGORY',
        });

        if (data) {
            dispatch(setDropdownOptions(data?.data));
        }
    };

    return {
        handleSubmit,
        handleIsSelected,
        handleNextClick,
        fetchDeviceTypes,
        widgetCreationLoader,
        deviceTypesLoader: isLoading || modelLoader,
        widgetError,
    };
};
